"use client"

import { useCallback, useEffect, useRef, useState } from "react"

/**
 * Horizontal product rail with native touch/scroll-snap swipe plus desktop
 * prev/next arrows and edge fades. Children are the already-rendered <li> cards
 * (server components), so this stays a thin client wrapper.
 */
export default function CarouselRail({
  children,
}: {
  children: React.ReactNode
}) {
  const ref = useRef<HTMLUListElement>(null)
  const [atStart, setAtStart] = useState(true)
  const [atEnd, setAtEnd] = useState(false)

  const update = useCallback(() => {
    const el = ref.current
    if (!el) return
    setAtStart(el.scrollLeft <= 4)
    setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 4)
  }, [])

  useEffect(() => {
    update()
    const el = ref.current
    if (!el) return
    el.addEventListener("scroll", update, { passive: true })
    window.addEventListener("resize", update)
    return () => {
      el.removeEventListener("scroll", update)
      window.removeEventListener("resize", update)
    }
  }, [update])

  const scrollBy = (dir: 1 | -1) => {
    const el = ref.current
    if (!el) return
    el.scrollBy({ left: dir * el.clientWidth * 0.8, behavior: "smooth" })
  }

  return (
    <div className="relative">
      <ul
        ref={ref}
        className="no-scrollbar -mx-6 flex snap-x snap-mandatory gap-4 overflow-x-auto px-6 pb-2 md:mx-0 md:gap-6 md:px-0"
        role="list"
      >
        {children}
      </ul>

      {/* Edge fades (desktop) */}
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-y-0 left-0 hidden w-16 bg-gradient-to-r from-white to-transparent transition-opacity md:block ${
          atStart ? "opacity-0" : "opacity-100"
        }`}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-y-0 right-0 hidden w-16 bg-gradient-to-l from-white to-transparent transition-opacity md:block ${
          atEnd ? "opacity-0" : "opacity-100"
        }`}
      />

      {/* Prev / next arrows (desktop) */}
      <button
        type="button"
        onClick={() => scrollBy(-1)}
        aria-label="Previous products"
        disabled={atStart}
        className="absolute left-2 top-[38%] z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#E5E5E5] bg-white text-[#1A1612] shadow-sm transition hover:border-[#3D5240] hover:text-[#3D5240] disabled:pointer-events-none disabled:opacity-0 md:flex"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
          <path d="M15 18l-6-6 6-6" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => scrollBy(1)}
        aria-label="Next products"
        disabled={atEnd}
        className="absolute right-2 top-[38%] z-10 hidden h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[#E5E5E5] bg-white text-[#1A1612] shadow-sm transition hover:border-[#3D5240] hover:text-[#3D5240] disabled:pointer-events-none disabled:opacity-0 md:flex"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden>
          <path d="M9 6l6 6-6 6" />
        </svg>
      </button>
    </div>
  )
}
